import { useState, useEffect } from 'react'
import { Col, Card, UncontrolledDropdown, DropdownMenu, DropdownItem } from 'reactstrap'
import { IconTrash, IconDotsVertical, IconEdit, IconCheck, IconBookmark } from '@tabler/icons-react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useCollectionService } from '../../_services/_index'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import CardTooltip from '../_molecules/CardTooltip'
import DeleteCollectionModal from '../_templates/DeleteCollectionModal'
import CreateCollectionModal from '../_templates/CreateCollectionModal'
import { useSelector } from 'react-redux'
import { Role } from '../../utility/types'
import { StyledDropdownToggle } from '../../styles/common'
import { Avatar } from 'dsm-react-lib'
import { useSasToken } from '../../_hooks/useSasToken'

export const StyledCol = styled(Col)`
  margin-bottom: -2rem;
`

const AppsContainer = styled.div`
  display: flex;
  padding: 0 0.5rem;
`

export const ImgContainer = styled.div`
  z-index: ${(props) => props.zindex};
  position: relative;
  margin-right: -2rem;
`

export const AppCount = styled.div`
  &:hover {
    cursor: pointer;
  }
`

const ActionButton = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
`
const AppName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: -webkit-fill-available;
`

const CollectionApps = ({
  item,
  uniqueSectionName = '',
  isCustom = false,
  workspaceCollection = [],
  setCollectionApps,
  updateCollectionsCallback
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { addCollectionItem, removeCollectionItem } = useCollectionService()
  const { appendToken } = useSasToken()

  const [foundCollectionInWorkspace, setfoundCollectionInWorkspace] = useState(false)
  const [isDeleteModalOpen, seIsDeleteModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const { roles } = useSelector((state) => state.userContextReducer.userContext)
  const isAdmin = roles?.includes(Role.Admin)

  useEffect(() => {
    const foundCollection = workspaceCollection?.find(
      (collectionWorkspace) => collectionWorkspace.collectionId === item.collectionId
    )
    setfoundCollectionInWorkspace(!!foundCollection)
  }, [workspaceCollection])

  const addCollectionForUser = async (item) => {
    const response = foundCollectionInWorkspace
      ? await removeCollectionItem(item)
      : await addCollectionItem(item)
    if (response?.status === 0) {
      if (setCollectionApps && foundCollectionInWorkspace) {
        setCollectionApps(
          workspaceCollection.filter((collection) => collection.collectionId !== item.collectionId)
        )
      } else {
        workspaceCollection.push(item)
        setCollectionApps(workspaceCollection)
      }

      setfoundCollectionInWorkspace((foundCollectionInWorkspace) => !foundCollectionInWorkspace)
    }
  }

  const onEdit = (collection) => {
    isCustom ? toggleEditModal() : navigate(`/edit-collection/${collection.uniqueName}`)
  }

  const toggleEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen)
  }

  const toggleDeleteModal = (collectionId, collectionName) => {
    seIsDeleteModalOpen(!isDeleteModalOpen)
  }

  const onDeleteModalSubmittedCallback = (updatedCollections, deletedItem) => {
    seIsDeleteModalOpen(!isDeleteModalOpen)
    updateCollectionsCallback(updatedCollections, deletedItem)
  }

  const onEditModalSubmittedCallback = async (updatedCollections) => {
    toggleEditModal()
    updateCollectionsCallback(updatedCollections)
    toast.success('Collection has been saved')
  }

  return (
    <>
      <StyledCol className="collectionApps" md={12} data-testid={`collections-${item.uniqueName}`}>
        <AppsContainer>
          {item.applications.slice(0, 4).map((app, index) => (
            <Link
              to={{
                pathname: `/apps/${app.uniqueName}`,
                state: {
                  from: location.pathname === '/workspace' ? 'Workspace' : 'Collections',
                  name: `${item.collectionName}`,
                  id: `${item.uniqueName}`
                }
              }}
              className="theme-text-heading"
              key={app.uniqueName}
              data-container="body"
              data-testid={`collectionApp-${app.uniqueName}`}>
              <ImgContainer zindex={item.applications.length - index + 1}>
                <Avatar
                  data-tooltip-id={`collection-app-${app.uniqueName}`}
                  className="img-fluid shadow-sm"
                  content={appendToken(app.applicationIcon)}
                  size="54px"
                  shape="rounded"
                  alt="logo"
                />
              </ImgContainer>
              <CardTooltip
                id={`collection-app-${app.uniqueName}`}
                key={app.uniqueName}
                item={app}
              />
            </Link>
          ))}
        </AppsContainer>
      </StyledCol>

      <Card
        className="border shadow-sm bg-surface p-2 pt-3"
        data-testid={`collection-${item.collectionName}`}>
        <ActionButton>
          {((isAdmin && location.pathname === '/collections') || isCustom) && (
            <UncontrolledDropdown>
              <StyledDropdownToggle tag="span">
                <IconDotsVertical size={15} />
              </StyledDropdownToggle>
              <DropdownMenu end>
                <DropdownItem
                  tag="a"
                  href="/"
                  className="w-100 flex"
                  onClick={(e) => {
                    e.preventDefault()
                    onEdit(item)
                  }}
                  data-testid="editCollection">
                  <IconEdit size={15} />
                  <span className="align-middle ms-50">Edit collection</span>
                </DropdownItem>

                <DropdownItem
                  tag="a"
                  href="/"
                  className="w-100 flex"
                  onClick={(e) => {
                    e.preventDefault()
                    toggleDeleteModal(item.collectionId, item.collectionName)
                  }}
                  data-testid="deleteCollection">
                  <IconTrash color={'red'} size={15} />
                  <span className="text-secondary align-middle ms-50">Delete collection</span>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          )}
        </ActionButton>
        <div className="pt-1">
          <Link
            to={{
              pathname:
                item.collectionId !== undefined
                  ? item.collectionType === 'Custom'
                    ? `/personal-collections/${item.uniqueName}`
                    : `/collections/${item.uniqueName}`
                  : '/app-store',
              state: {
                from: location.pathname.slice(1)
              }
            }}
            className="d-inline pointer btn-link">
            <AppName id={`collection-${item.collectionId}`}>
              <strong>{item.collectionName}</strong>
            </AppName>
          </Link>
        </div>

        <div className="d-flex justify-content-between pt-1">
          <AppCount
            onClick={() => {
              Object.hasOwn(item, 'owner')
                ? navigate(`/personal-collections/${item.uniqueName}`)
                : navigate(`/collections/${item.uniqueName}`)
            }}>
            {item.applications.length} {item.applications.length === 1 ? 'app' : 'apps'}
          </AppCount>
          {!isCustom && (
            <div
              id={`btn-item-${item.uniqueName}-${uniqueSectionName}`}
              className={`small pointer flex ${
                foundCollectionInWorkspace ? 'text-secondary' : 'text-success'
              }`}
              onClick={() => addCollectionForUser(item)}
              data-testid={foundCollectionInWorkspace ? 'removeCollection' : 'addCollection'}>
              {foundCollectionInWorkspace ? (
                <>
                  <IconCheck size={14} /> <span>Remove collection </span>
                </>
              ) : (
                <>
                  <IconBookmark size={14} /> Add collection{' '}
                </>
              )}
            </div>
          )}
        </div>
      </Card>
      {isDeleteModalOpen && (
        <DeleteCollectionModal
          open={isDeleteModalOpen}
          onCloseModal={toggleDeleteModal}
          onHandleCallback={onDeleteModalSubmittedCallback}
          collectionData={item}
          isCustom={isCustom}
        />
      )}
      {isEditModalOpen && (
        <CreateCollectionModal
          isOpen={isEditModalOpen}
          toggle={toggleEditModal}
          onSubmittedCallback={onEditModalSubmittedCallback}
          collectionData={item}
          isEditing={true}
        />
      )}
    </>
  )
}
export default CollectionApps
